// Patent claims exercised: P-005 Claims 1-6 (AI recipe generation with health constraints)
import { API_BASE } from "../../config";
import React, { useState } from 'react';
import XaiPromptPanel from '../XaiPromptPanel';
import { CitationsButton } from '../Citations';
import { citationCount } from '../../utils/xaiHelpers';
import MedicalDisclaimer from '../MedicalDisclaimer';
import DomainValidationModal from '../DomainValidationRetryModal';
import { useDomainValidation } from '../../hooks/useDomainValidation';
import { handleAIError } from '../../utils/aiErrorHandler';
import CostChip from '../CostChip';
import { useCreditSpend } from '../../contexts/CreditSpendContext';

const RecipeGenerator = ({ user, token, fetchWithAuth, onClose, onBack }) => {
  // Auth-aware fetch with 401 retry (falls back to raw fetch if fetchWithAuth not provided)
  const authFetch = fetchWithAuth || ((url, opts) => fetch(url, { ...opts, headers: { ...opts?.headers, 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }));
  const { spend } = useCreditSpend();  // #77 ai_recipe / optimize_recipe = 2 credits each (chip + low-balance confirm)
  const [mode, setMode] = useState(null); // 'new' or 'optimize'
  const [prompt, setPrompt] = useState('');
  const [mealType, setMealType] = useState('breakfast');
  const [loading, setLoading] = useState(false);
  const [generatedRecipe, setGeneratedRecipe] = useState(null);
  const [recipePromptData, setRecipePromptData] = useState(null);
  const [showXai, setShowXai] = useState(false);
  const [userRecipes, setUserRecipes] = useState([]);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const { domainValidation, handleValidationResponse, clearValidation } = useDomainValidation();

  const loadUserRecipes = async () => {
    try {
      const response = await authFetch(`${API_BASE}/api/dashboard/user-recipes?source=manual_only&limit=100`);
      if (response.ok) {
        const data = await response.json();
        setUserRecipes(data.recipes || []);
      }
    } catch (error) {
      console.error('Failed to load recipes:', error);
    }
  };

  const generateNewRecipe = async () => {
    setLoading(true);
    setSaved(false);
    try {
      const response = await authFetch(`${API_BASE}/api/chat/ai-recipe`, {
        method: 'POST',
        body: JSON.stringify({
          prompt,
          meal_type: mealType,
          include_profile: true
        })
      });

      const validationResult = await handleValidationResponse(response, 'recipe');
      if (validationResult === true) {
        const data = await response.json();
        setGeneratedRecipe(data.recipe);
        setRecipePromptData(data.prompt_data || null);
        setShowXai(false);
      } else if (validationResult === null) {
        if (response.status === 409) {
          // Removed by the health-profile safety screen; the server's message says why.
          const err = await response.json().catch(() => null);
          alert(err?.detail || 'This recipe conflicts with your health profile, so it isn\'t shown.');
        } else if (await handleAIError(response)) {
          // unified AI limit modal shown
        } else {
          alert('Failed to generate recipe');
        }
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error generating recipe');
    } finally {
      setLoading(false);
    }
  };

  const optimizeRecipe = async () => {
    if (!selectedRecipe) {
      alert('Please select a recipe to optimize');
      return;
    }

    setLoading(true);
    setSaved(false);
    try {
      const response = await authFetch(`${API_BASE}/api/chat/optimize-recipe`, {
        method: 'POST',
        body: JSON.stringify({
          recipe_id: selectedRecipe.id,
          optimize_for_profile: true,
          match_macros: true,
          substitute_allergens: true
        })
      });

      if (response.ok) {
        const data = await response.json();
        setGeneratedRecipe(data.optimized_recipe);
        setRecipePromptData(data.prompt_data || null);
        setShowXai(false);
      } else if (response.status === 409) {
        // Removed by the health-profile safety screen; the server's message says why.
        const err = await response.json().catch(() => null);
        alert(err?.detail || 'This recipe conflicts with your health profile, so it isn\'t shown.');
      } else if (await handleAIError(response)) {
        // unified AI limit modal shown
      } else {
        alert('Failed to optimize recipe');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error optimizing recipe');
    } finally {
      setLoading(false);
    }
  };

  const saveRecipe = async () => {
    if (!generatedRecipe || saved) return;
    setSaving(true);
    try {
      const response = await authFetch(`${API_BASE}/api/chat/save-optimized-recipe`, {
        method: 'POST',
        body: JSON.stringify({
          recipe: generatedRecipe,
          original_recipe_id: selectedRecipe?.id || null
        })
      });
      const validationResult = await handleValidationResponse(response, 'recipe');
      if (validationResult === true) {
        const data = await response.json();
        setSaved(true);
        // Auto-generate AI image for the saved recipe (fire-and-forget)
        if (data.recipe_id) {
          authFetch(`${API_BASE}/api/recipes/${data.recipe_id}/generate-image`, {
            method: 'POST',
          }).catch(() => {/* fire-and-forget — image generates server-side even if gateway times out */});
        }
      } else if (validationResult === null) {
        alert('Failed to save recipe');
      }
    } catch (error) {
      console.error('Error saving:', error);
      alert('Error saving recipe');
    } finally {
      setSaving(false);
    }
  };

  // Shared recipe result renderer
  const renderRecipeResult = (title) => (
    <div className="rg-modalGenerated">
      <div className="rg-modalContentGenerated">
        <div className="rg-headerGenerated">
          <h3 className="rg-titleGenerated">{title}</h3>
          <button onClick={() => { setGeneratedRecipe(null); setSaved(false); }} className="rg-closeButtonGenerated">&times;</button>
        </div>

        <div className="rg-recipeDisplay">
          <h2 className="rg-recipeName">{generatedRecipe.name}</h2>

          <div className="rg-nutritionBox">
            <strong>Nutrition Facts (per serving):</strong>
            <div className="rg-nutritionGrid">
              <div>Calories: {generatedRecipe.calories || 'N/A'}</div>
              <div>Protein: {generatedRecipe.protein || 'N/A'}g</div>
              <div>Carbs: {generatedRecipe.carbs || 'N/A'}g</div>
              <div>Fat: {generatedRecipe.fat || 'N/A'}g</div>
            </div>
          </div>

          <div className="rg-ingredientsSection">
            <strong>Ingredients:</strong>
            <div className="rg-ingredientsContent">
              {Array.isArray(generatedRecipe.ingredients)
                ? generatedRecipe.ingredients.map((ing, i) => <div key={i}>{ing}</div>)
                : generatedRecipe.ingredients || 'No ingredients listed'}
            </div>
          </div>

          <div className="rg-instructionsSection">
            <strong>Instructions:</strong>
            <div className="rg-instructionsContent">
              {generatedRecipe.instructions || 'No instructions available'}
            </div>
          </div>

          {generatedRecipe.health_notes && (
            <div className="rg-infoBox">
              <p className="rg-infoHeading">Health Notes:</p>
              <p>{generatedRecipe.health_notes}</p>
            </div>
          )}

          {recipePromptData && (
            <>
              <CitationsButton
                open={showXai}
                count={citationCount(recipePromptData, 'Recipe Generator')}
                onToggle={() => setShowXai(!showXai)}
              />
              {showXai && <XaiPromptPanel promptData={recipePromptData} featureName="Recipe Generator" />}
            </>
          )}

          <MedicalDisclaimer variant="ai" />

          <div className="rg-footer">
            <button
              onClick={saved ? onClose : saveRecipe}
              disabled={saving}
              className="rg-saveButton"
            >
              {saving ? 'Saving...' : saved ? 'Saved — Close' : 'Save Recipe'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Domain validation modal (renders on top of any current view)
  const dvModal = domainValidation && (
    <DomainValidationModal
      isOpen={true}
      mode={domainValidation.mode}
      rejectionMessage={domainValidation.message}
      entityType={domainValidation.entityType}
      onClose={clearValidation}
    />
  );

  // Loading spinner while AI is working
  if (loading) {
    return (
      <>{dvModal}<div className="rg-modalGenerated">
        <div className="rg-modalContentGenerated">
          <div className="rg-headerGenerated">
            <h3 className="rg-titleGenerated">
              {mode === 'optimize' ? 'Optimizing Recipe...' : 'Generating Recipe...'}
            </h3>
          </div>
          <div className="rg-recipeDisplay" style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div className="rg-spinner"></div>
            <p style={{ marginTop: '20px', color: '#1E3A5F', fontSize: '16px', fontWeight: 500 }}>
              {mode === 'optimize'
                ? 'AI is analyzing your health profile and optimizing this recipe...'
                : 'AI is creating a personalized recipe based on your profile...'}
            </p>
            <p style={{ marginTop: '8px', color: '#6B7280', fontSize: '13px' }}>
              This may take 10–20 seconds
            </p>
          </div>
        </div>
      </div></>
    );
  }

  // Mode Selection
  if (!mode) {
    return (
      <>{dvModal}<div className="rg-modal">
        <div className="rg-modalContent">
          <div className="rg-header">
            <h3 className="rg-title">AI Recipe Generator</h3>
            <button onClick={onBack} className="rg-closeButton">&times;</button>
          </div>

          <div className="rg-modeSelector">
            <p className="rg-modeSelectorText">What would you like to do?</p>

            <div
              onClick={() => setMode('new')}
              className="rg-modeCardNew"
              onMouseOver={(e) => e.currentTarget.style.borderColor = '#1E3A5F'}
              onMouseOut={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
            >
              <h4 className="rg-modeCardNewTitle">Generate New Recipe</h4>
              <p className="rg-modeCardNewDescription">
                AI creates a recipe from scratch based on your profile, preferences, and goals
              </p>
            </div>

            <div
              onClick={() => { setMode('optimize'); loadUserRecipes(); }}
              className="rg-modeCardOptimize"
              onMouseOver={(e) => e.currentTarget.style.borderColor = '#1E3A5F'}
              onMouseOut={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
            >
              <h4 className="rg-modeCardOptimizeTitle">Optimize Existing Recipe</h4>
              <p className="rg-modeCardOptimizeDescription">
                Select a manually entered or uploaded recipe for AI to adapt to your health profile
              </p>
            </div>
          </div>
        </div>
      </div></>
    );
  }

  // Generate New Recipe Mode — form
  if (mode === 'new' && !generatedRecipe) {
    return (
      <>{dvModal}<div className="rg-modalNew">
        <div className="rg-modalContentNew">
          <div className="rg-headerNew">
            <h3 className="rg-titleNew">Generate New Recipe</h3>
            <button onClick={() => setMode(null)} className="rg-closeButtonNew">&times;</button>
          </div>

          <div className="rg-formSection">
            <label className="rg-promptLabel">
              Tell me what you're craving:
            </label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="E.g., High protein breakfast with eggs or just Something Italian"
              className="rg-promptTextarea"
            />

            <label className="rg-mealTypeLabel">
              Meal Type:
            </label>
            <div className="rg-mealTypeOptions">
              {['breakfast', 'lunch', 'dinner', 'snack'].map(type => (
                <label key={type} className="rg-mealTypeOption">
                  <input
                    type="radio"
                    value={type}
                    checked={mealType === type}
                    onChange={(e) => setMealType(e.target.value)}
                  />
                  <span className="rg-mealTypeText">{type}</span>
                </label>
              ))}
            </div>

            <div className="rg-infoBox">
              <p className="rg-infoHeading">AI will automatically consider:</p>
              <ul className="rg-considerationsList">
                <li>Your calorie and macro goals</li>
                <li>Food allergies and restrictions</li>
                <li>Cuisine preferences</li>
                <li>Your pantry items</li>
              </ul>
            </div>

            <MedicalDisclaimer variant="ai" />

            <button
              onClick={() => spend('ai_recipe', 'Generate this recipe?', generateNewRecipe)}
              disabled={!prompt.trim()}
              className="rg-generateButton" style={{ backgroundColor: !prompt.trim() ? '#D1D5DB' : '#1E3A5F', cursor: !prompt.trim() ? 'not-allowed' : 'pointer' }}
            >
              Generate Recipe <CostChip apiType="ai_recipe" />
            </button>
          </div>
        </div>
      </div></>
    );
  }

  // Generate New Recipe — result (with Regenerate)
  if (mode === 'new' && generatedRecipe) {
    return <>{dvModal}{renderRecipeResult('Your AI-Generated Recipe')}</>;
  }

  // Optimize Recipe Mode — form
  if (mode === 'optimize' && !generatedRecipe) {
    return (
      <>{dvModal}<div className="rg-modalOptimize">
        <div className="rg-modalContentOptimize">
          <div className="rg-headerOptimize">
            <h3 className="rg-titleOptimize">Optimize Existing Recipe</h3>
            <button onClick={() => setMode(null)} className="rg-closeButtonOptimize">&times;</button>
          </div>

          <div className="rg-formSectionOptimize">
            <label className="rg-recipeSelectLabel">
              Select a recipe to optimize:
            </label>

            <select
              value={selectedRecipe?.id || ''}
              onChange={(e) => {
                const recipe = userRecipes.find(r => r.id === parseInt(e.target.value));
                setSelectedRecipe(recipe);
              }}
              className="rg-recipeSelect"
            >
              <option value="">Choose a recipe...</option>
              {userRecipes.map(recipe => (
                <option key={recipe.id} value={recipe.id}>
                  {recipe.name} ({recipe.calories || 'N/A'} cal)
                </option>
              ))}
            </select>

            {selectedRecipe && (
              <div className="rg-recipePreview">
                <strong>{selectedRecipe.name}</strong>
                <p className="rg-recipeDescription">
                  {selectedRecipe.description || 'No description available'}
                </p>
              </div>
            )}

            <div className="rg-goalsBox">
              <p className="rg-goalsHeading">What AI will do:</p>
              <ul className="rg-goalsList">
                <li>Optimize a manually entered or uploaded recipe to meet your specific profile</li>
                <li>Match your macro targets</li>
                <li>Substitute allergens with safe alternatives</li>
                <li>Adjust portions for your calorie goal</li>
                <li>Use ingredients from your pantry when possible</li>
              </ul>
            </div>

            <MedicalDisclaimer variant="ai" />

            <button
              onClick={() => spend('optimize_recipe', 'Optimize this recipe?', optimizeRecipe)}
              disabled={!selectedRecipe}
              className="rg-optimizeButton" style={{ backgroundColor: !selectedRecipe ? '#D1D5DB' : '#1E3A5F', cursor: !selectedRecipe ? 'not-allowed' : 'pointer' }}
            >
              Optimize This Recipe <CostChip apiType="optimize_recipe" />
            </button>
          </div>
        </div>
      </div></>
    );
  }

  // Optimize Recipe — result (NO Regenerate)
  if (mode === 'optimize' && generatedRecipe) {
    return <>{dvModal}{renderRecipeResult('Your Optimized Recipe')}</>;
  }

  return dvModal || null;
};

export default RecipeGenerator;
